/**
 * Helper functions for generating notarization HTML templates
 */

/**
 * Formats a date string to a readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date or placeholder
 */
const formatDate = (dateString) => {
  if (!dateString) return "_______";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const getAcknowledgementTemplate = ({
  notarization = {},
  header = "",
}) => {
  const formattedDate = formatDate(notarization.date);
  const location = notarization.location || "_______";
  const documentHeader = header || "_______";

  return `
  <p><strong>IN WITNESS WHEREOF</strong>, the ____ hereunto affix my/our signature this <u>${formattedDate}</u> at <u>${location}</u>, Philippines.</p>
  <p>&nbsp;</p><p>&nbsp;</p>

  ${MARKERS.SIGNATURE_BLOCK_START_HTML}
    <div style="width:100%; display:flex; flex-direction:column; gap:22px; align-items:center;">
      <div style="display:flex; width:80%; justify-content:space-between; gap:40px;">
        <div style="flex:1; text-align:center;">
            <p><u>_________________________</u></p>
            <p>Affiant</p>
        </div>
        <div style="flex:1; text-align:center;">
            <p><u>_________________________</u></p>
            <p>Witness</p>
        </div>
      </div>
    </div>
    ${MARKERS.SIGNATURE_BLOCK_END_HTML}

    <p>&nbsp;</p>
    <h2 style="text-align:center;">
      <span style="font-size:18px;"><strong><u>${documentHeader.toUpperCase()}</u></strong></span>
    </h2>
    <p>&nbsp;</p>
    
    <p>Republic of the Philippines &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; }&nbsp;</p>
    <p>${location.toUpperCase()} &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; } S.S.</p><p>&nbsp;</p>
    
    <p><strong>BEFORE ME</strong>, a Notary Public, in and for <u>${location}</u>, Philippines, this <u>${formattedDate}</u>, the undersigned Affiant/Affiants personally appeared:</p>

    ${MARKERS.AFFIANT_TABLE_START_HTML}
    <table class="table ck-table-resized" style="border-style:solid;width:98.56%;">
      <colgroup><col style="width:29.14%;"><col style="width:13.6%;"><col style="width:20.9%;"><col style="width:15.56%;"><col style="width:20.8%;"></colgroup>
      <thead>
        <tr>
          <th style="text-align:center;">Name</th>
          <th style="text-align:center;">Valid ID Type</th>
          <th style="text-align:center;">ID Number</th>
          <th style="text-align:center;">Expiry Date</th>
          <th style="text-align:center;">Date/Place of Issue</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="text-align:center;">&nbsp;</td>
          <td style="text-align:center;">&nbsp;</td>
          <td style="text-align:center;">&nbsp;</td>
          <td style="text-align:center;">&nbsp;</td>
          <td style="text-align:center;">&nbsp;</td>
        </tr>
      </tbody>
    </table>
    ${MARKERS.AFFIANT_TABLE_END_HTML}

    <p><strong>known to me and to me known to be</strong> the same person/people who executed the foregoing <u>${documentHeader}</u> consisting of <u>@page</u> (@page) page/s, including this page where the acknowledgement is written, and he/she/they acknowledged to me that the same is his/her/their free and voluntary act and deed.</p>
    <p>&nbsp;</p>
    <p><strong>WITNESS MY HAND AND SEAL</strong> on the day and place first above-written.</p>
  `.trim();
};

export const getJuratTemplate = (notarization = {}) => {
  const formattedDate = formatDate(notarization.date);
  const location = notarization.location || "Cebu City";

  return `
  <p><strong>SUBSCRIBED AND SWORN TO</strong> before me this <u>${formattedDate}</u>, in <u>${location}</u>, Philippines; the undersigned Affiant/Affiants after exhibiting to me his/her/their valid Government ID as proof of his/her/their identity:&nbsp;</p>
  <p>&nbsp;</p>
  
  ${MARKERS.AFFIANT_TABLE_START_HTML}
  <table class="table ck-table-resized" style="border-style:solid;width:98.56%;">
    <colgroup><col style="width:29.14%;"><col style="width:13.6%;"><col style="width:20.9%;"><col style="width:15.56%;"><col style="width:20.8%;"></colgroup>
    <thead>
      <tr>
        <th style="text-align:center;">Name</th>
        <th style="text-align:center;">Valid ID Type</th>
        <th style="text-align:center;">ID Number</th>
        <th style="text-align:center;">Expiry Date</th>
        <th style="text-align:center;">Date/Place of Issue</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="text-align:center;">&nbsp;</td>
        <td style="text-align:center;">&nbsp;</td>
        <td style="text-align:center;">&nbsp;</td>
        <td style="text-align:center;">&nbsp;</td>
        <td style="text-align:center;">&nbsp;</td>
      </tr>
    </tbody>
  </table>
  ${MARKERS.AFFIANT_TABLE_END_HTML}

  <p>&nbsp;</p>
  <p>I hereby certify that I have personally examined the Affiant/Affiants and I am satisfied that he/she voluntarily executed and understood the facts narrated in this.</p>
`.trim();
};

export const getSecretarialResolutionTemplate = (
  notarization = {},
  from,
  to
) => {
  const company = from?.name || "__________________";
  const formattedDate = formatDate(notarization.date);
  const location = notarization.location || "Cebu City";
  const resolutionTitle = notarization.resolutionTitle || "";
  const resolutionNumber = notarization.resolutionNumber || "_____";
  const resolutionYear = notarization.resolutionYear || "_____";
  const secretaryName = to?.name || "__________________";
  const secretaryAddress = to?.address || "__________________";

  return `
  <h2 style="text-align:center;"><strong>${company.toUpperCase()}</strong></h2>
  
  <p style="text-align:center;"><strong>Board Resolution ${resolutionNumber}</strong><br>Series of ${resolutionYear}</p>
  <p style="text-align:center;">&nbsp;</p>
  <h3 style="text-align:center;"><strong>${resolutionTitle}</strong></h3>
  <p><strong>WHEREAS</strong> ____</p>
  <p>&nbsp;</p>
  <p><strong>HEREBY GRANTING</strong> ____</p>
  <p>&nbsp;</p>
  <p><strong>NOW THEREFORE, BE IT RESOLVED AS IT IS HEREBY RESOLVED THAT </strong>____</p>
  <p>&nbsp;</p>
  <p><strong>IN WITNESS WHEREOF</strong>, ____</p>
  <p>&nbsp;</p>
  
  <div style="display:none;" data-marker="signature-block-start">&nbsp;</div>
  <div style="width:100%; display:flex; flex-direction:column; gap:22px; align-items:center;">
    <div style="display:flex; width:80%; justify-content:space-between; gap:40px;">
      <div style="flex:1; text-align:center;">
        <p><u>_______________</u></p>
        <p>Board Member/Position<br>TIN: _______________</p>
      </div>
      <div style="flex:1; text-align:center;">
        <p><u>_______________</u></p>
        <p>Board Member/Position<br>TIN: _______________</p>
      </div>
    </div>
  </div>

  <div style="display:none;" data-marker="signature-block-end">&nbsp;</div>
  <p>&nbsp;</p>
  <p>
    <span style="font-size:12px;"><i>Consent of Agents-to-Act:</i></span>
  </p>
  <p>&nbsp;</p>
  <hr>
  <p style="text-align:center;">&nbsp;</p>
  <p class="p1">REPUBLIC OF THE PHILIPPINES &nbsp; &nbsp; &nbsp; &nbsp;}</p>
  <p class="p1">${location.toUpperCase()} &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;} S.S.</p>
  <p class="p1">&nbsp;</p>
  <p class="p1">&nbsp;</p>
  <h3 style="text-align:center;"><strong>SECRETARY'S CERTIFICATE</strong></h3>
  <p style="text-align:center;">&nbsp;</p>
  <p>I, <u>${secretaryName}</u>, of legal age, married, Filipino citizen, and a resident of <u>${secretaryAddress}</u>, after having duly sworn to in accordance with law hereby depose and say that:</p>
  <p>&nbsp;</p>
  <ol>
    <li data-list-item-id="e14cf65390073511ca6e7b950f137490d">
      <p style="text-align:center;">I am the incumbent Corporate Secretary of <u>${company}</u>;</p>
    </li>
    <li data-list-item-id="e9e461597018fd44e3eac713a56d81202">
      <p>The following is a true and correct copy of the Board Resolution issued on <u>${formattedDate}</u>;</p>
    </li>
  </ol>
  <p>&nbsp;</p>
  <p style="text-align:center;"><strong><u>RESOLUTION ______ Series of 2025</u></strong></p>

  <blockquote>
    <p style="text-align:justify;">
      <i><strong>“ </strong></i>
      <strong>NOW, THEREFORE, BE IT RESOLVED, </strong>that the Corporate Secretary shall have <strong>Special Power of Attorney to sign for and on behalf of the Corporation to purchase and/or pay</strong> from <strong>_________.</strong> for the herein-attached specified _______ <strong>for and on behalf of the Corporation, specifically described as follows, to wit:__</strong>
      <i><strong> ”</strong></i>
    </p>
  </blockquote>
  
  <p style="text-align:center;">&nbsp;</p>
  <p style="text-align:center;">&nbsp;</p>
  <p><strong>IN WITNESS WHEREOF</strong>, the party/parties have hereunto set their hands this <u>${formattedDate}</u> at <u>${location}</u>, Philippines.</p>
  <p>&nbsp;</p>

  <div style="display:none;" data-marker="centered-block">&nbsp;</div>
  <div style="width:100%; display:flex; flex-direction:column; gap:22px; align-items:center;">
    <div style="display:flex; width:80%; justify-content:center;">
      <div style="flex:1; max-width:300px; text-align:center;">
        <p>____________________________</p>
        <p><strong>Secretary/Treasurer & <br> Board of Director<br>TIN:_____________________<br>LTO:_____________________</strong></p>
      </div>
    </div>
  </div>


  <p class="p1">&nbsp;</p>
  <p class="p1"><strong>SUBSCRIBED AND SWORN TO</strong> before me this <u>${formattedDate}</u>, at <u>${location}</u>, Philippines, the undersigned Affiant/Affiants after exhibiting to me his/her/their valid Government ID as proof of his/her/their identity. I hereby certify that I have personally examined the Affiant/Affiants and I am satisfied that he/she/they voluntarily executed and understood the facts narrated in this __________.</p>
`.trim();
};

/**
 * Gets the appropriate template based on acknowledgement type and notarization type
 * @param {string} ackType - Type of acknowledgement ('sec-res', 'acknowledgement')
 * @param {Object} formData - Form data containing notarization info and header
 * @param {string} formData.notarizationType - Type of notarization ('jurat' or 'non-jurat')
 * @returns {string} HTML template
 */
export const getNotarizationTemplate = (ackType, formData = {}) => {
  const {
    notarization = {},
    header = "",
    notarizationType = "",
    from = {},
    to = {},
  } = formData;

  switch (ackType) {
    case "sec-res":
      // Secretary's Resolution uses jurat format by default
      return getSecretarialResolutionTemplate(notarization, from, to);

    case "ack":
    case "acknowledgement":
      // Check notarizationType to determine which template to use
      if (notarizationType === "jurat") {
        return getJuratTemplate(notarization);
      } else {
        // Default to acknowledgement template for 'non-jurat' or undefined
        return getAcknowledgementTemplate({ notarization, header });
      }

    default:
      return "";
  }
};

// Marker constants for identifying sections in the HTML
export const MARKERS = {
  // detection
  SIGNATURE_BLOCK_START: 'data-marker="signature-block-start"',
  SIGNATURE_BLOCK_END: 'data-marker="signature-block-end"',
  AFFIANT_TABLE_START: 'data-marker="affiant-table-start"',
  AFFIANT_TABLE_END: 'data-marker="affiant-table-end"',

  // insertion
  AFFIANT_TABLE_START_HTML:
    '<div style="display:none;" data-marker="affiant-table-start"></div>',
  AFFIANT_TABLE_END_HTML:
    '<div style="display:none;" data-marker="affiant-table-end"></div>',
  SIGNATURE_BLOCK_START_HTML:
    '<div style="display:none;" data-marker="signature-block-start"></div>',
  SIGNATURE_BLOCK_END_HTML:
    '<div style="display:none;" data-marker="signature-block-end"></div>',
};

/**
 * Inserts an affiant table with data into the CKEditor
 * Replaces existing table if markers are found
 * @param {Object} editor - CKEditor instance
 * @param {Array} affiants - Array of affiant objects
 */
export const insertAffiantTableWithData = (editor, affiants = []) => {
  if (!editor) return;

  let tableHTML = `
${MARKERS.AFFIANT_TABLE_START_HTML}
<table>
  <thead>
    <tr>
      <th>Name</th>
      <th>Valid ID Type</th>
      <th>ID Number</th>
      <th>Expiry Date</th>
      <th>Date/Place of Issue</th>
    </tr>
  </thead>
  <tbody>`;

  if (affiants.length > 0) {
    affiants.forEach((affiant) => {
      tableHTML += `
    <tr>
      <td>${affiant.name || "_________________________"}</td>
      <td>${affiant.idType || "_________________________"}</td>
      <td>${affiant.idNumber || "_________________________"}</td>
      <td>${affiant.expiryDate || "_________________________"}</td>
      <td>${affiant.issueInfo || "_________________________"}</td>
    </tr>`;
    });
  } else {
    tableHTML += `
    <tr>
      <td>_________________________</td>
      <td>_________________________</td>
      <td>_________________________</td>
      <td>_________________________</td>
      <td>_________________________</td>
    </tr>`;
  }

  tableHTML += `
  </tbody>
</table>
${MARKERS.AFFIANT_TABLE_END_HTML}`;

  // Get current content (raw HTML with comments preserved)
  const currentContent = editor.getData();

  // Check if markers exist
  const hasMarkers =
    currentContent.includes(MARKERS.AFFIANT_TABLE_START) &&
    currentContent.includes(MARKERS.AFFIANT_TABLE_END);

  if (hasMarkers) {
    const startAttr = MARKERS.AFFIANT_TABLE_START;
    const endAttr = MARKERS.AFFIANT_TABLE_END;

    const regex = new RegExp(
      `<div[^>]*${startAttr}[^>]*>[\\s\\S]*?<\\/div>[\\s\\S]*?<div[^>]*${endAttr}[^>]*>[\\s\\S]*?<\\/div>`,
      "g"
    );

    const newContent = currentContent.replace(regex, tableHTML);
    editor.setData(newContent);
  } else {
    // Insert new table at cursor position
    const viewFragment = editor.data.processor.toView(tableHTML);
    const modelFragment = editor.data.toModel(viewFragment);
    editor.model.insertContent(modelFragment);
  }
};

/**
 * Inserts a signature block with witness data into the CKEditor
 * Replaces existing block if markers are found
 * @param {Object} editor - CKEditor instance
 * @param {Array} witnesses - Array of witness objects
 */
export const insertSignatureBlockWithData = (editor, witnesses = []) => {
  if (!editor) return;

  let signatureHTML = `${MARKERS.SIGNATURE_BLOCK_START_HTML}`;

  // --- FLEXBOX SIGNATURE BLOCK, NO TABLES ----
  signatureHTML += `
<div style="width:100%; display:flex; flex-direction:column; gap:22px; align-items:center;">
`;

  // If no witnesses, show two default placeholders
  if (witnesses.length === 0) {
    signatureHTML += `
    <div style="display:flex; width:80%; justify-content:space-between; gap:40px;">
        <div style="flex:1; text-align:center;">
            <p><u>_________________________</u></p>
            <p>Affiant/Witness</p>
        </div>
        <div style="flex:1; text-align:center;">
            <p><u>_________________________</u></p>
            <p>Affiant/Witness</p>
        </div>
    </div>
</div>
${MARKERS.SIGNATURE_BLOCK_END_HTML}`;
  } else {
    // Process witnesses in rows of two
    for (let i = 0; i < witnesses.length; i += 2) {
      const w1 = witnesses[i];
      const w2 = witnesses[i + 1];

      // ONE signature left → center it.
      const isSingle = !w2;

      signatureHTML += `
        <div style="
            display:flex;
            width:80%;
            justify-content:${isSingle ? "center" : "space-between"};
            gap:40px;
        ">
            <div style="flex:1; text-align:center; max-width:300px;">
                <p><u>${w1.name || "_________________________"}</u></p>
                <p>${w1.label || "Affiant/Witness"}</p>
            </div>

            ${
              !isSingle
                ? `
            <div style="flex:1; text-align:center; max-width:300px;">
                <p><u>${w2.name || "_________________________"}</u></p>
                <p>${w2.label || "Affiant/Witness"}</p>
            </div>
            `
                : ""
            }
        </div>`;
    }

    signatureHTML += `</div>${MARKERS.SIGNATURE_BLOCK_END_HTML}`;
  }
  // Get current content
  const currentContent = editor.getData();

  // Check if markers exist
  const hasMarkers =
    currentContent.includes(MARKERS.SIGNATURE_BLOCK_START) &&
    currentContent.includes(MARKERS.SIGNATURE_BLOCK_END);

  if (hasMarkers) {
    const startAttr = MARKERS.SIGNATURE_BLOCK_START;
    const endAttr = MARKERS.SIGNATURE_BLOCK_END;

    const regex = new RegExp(
      `<div[^>]*${startAttr}[^>]*>[\\s\\S]*?<\\/div>[\\s\\S]*?<div[^>]*${endAttr}[^>]*>[\\s\\S]*?<\\/div>`,
      "g"
    );

    const newContent = currentContent.replace(regex, signatureHTML);
    editor.setData(newContent);
  } else {
    // Insert new block at cursor position
    const viewFragment = editor.data.processor.toView(signatureHTML);
    const modelFragment = editor.data.toModel(viewFragment);
    editor.model.insertContent(modelFragment);
  }
};
